import { Box, Stack } from "@mui/material";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormTextField,
  FormAutocomplete,
  SaveAndCancelButton,
} from "../../../../components/form";
import { updateUserProfile } from "../../../../api/profile";
import { useMutationWithDispatch } from "../../../../hooks/useMutationWithDispatch";
import { useAppSelector } from "../../../../store";
import {
  editBasicDetailsSchema,
  EditBasicDetailsFormData,
} from "./config";
import { EditMyBasicDetailsProps, UpdateProfileResponse } from "./types";
import { getUpdatePayload, mapDataToFormValues } from "./utils";
import { ENUM_USER_ROLE } from "../../../../enums";

const BIO_PLACEHOLDER =
  "John Smith is an accomplished cybersecurity professional with over 10 years of experience in the field. He holds a Bachelor's degree in Computer Science and various industry certifications including CISSP, CISM, and CEH. John has a proven track record of developing and implementing successful cybersecurity strategies to mitigate risks and protect organizational assets. He is skilled in performing vulnerability assessments, conducting penetration testing, and leading incident response teams. John is passionate about staying up-to-date with the latest cybersecurity trends and technologies to better serve his clients. He is committed to providing exceptional service and ensuring the confidentiality, integrity, and availability of his clients' information.";

export default function EditMyBasicDetails({
  data,
  onCancel,
  onSubmit,
}: EditMyBasicDetailsProps) {
  const lists = useAppSelector((state) => state.lists);

  const industryOptions = useMemo(
    () => lists?.industries?.data || [],
    [lists?.industries?.data]
  );
  const titleOptions = useMemo(
    () => lists?.titles?.data?.filter((item) => item?.types?.includes(ENUM_USER_ROLE.AMBASSADOR)) || [],
    [lists?.titles?.data]
  );
  const noOfEmpOptions = useMemo(
    () => lists?.no_of_emps?.data || [],
    [lists?.no_of_emps?.data]
  );

  const defaultFormValues = useMemo(() => {
    const base = mapDataToFormValues(data);
    let industry = base.industry;
    let no_of_emp = base.no_of_emp;

    if (base.industry && industryOptions.length) {
      const byId = industryOptions.find((o: any) => o.id === base.industry);
      if (byId) industry = byId.label;
    }
    if (base.no_of_emp && noOfEmpOptions.length) {
      const byLabel = noOfEmpOptions.find(
        (o: any) => o.label === base.no_of_emp
      );
      const byId = noOfEmpOptions.find((o: any) => o.id === base.no_of_emp);
      if (byLabel) no_of_emp = byLabel.id;
      else if (byId) no_of_emp = byId.id;
    }

    return { ...base, industry, no_of_emp };
  }, [
    data?.industry,
    data?.title,
    data?.description,
    data?.no_of_emp,
    industryOptions,
    noOfEmpOptions,
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditBasicDetailsFormData>({
    resolver: zodResolver(editBasicDetailsSchema),
    defaultValues: defaultFormValues,
  });

  const { mutateAsync: updateProfile, isPending: submitting } =
    useMutationWithDispatch<
      UpdateProfileResponse,
      Record<string, string | undefined>
    >((payload) => updateUserProfile(payload));

  const onFormSubmit = async (formData: EditBasicDetailsFormData) => {
    const payload = getUpdatePayload(formData);
    const res = await updateProfile(payload);
    if (res?.success) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <Stack gap={2}>
        <Box>
          <FormAutocomplete
            name="industry"
            control={control}
            label="Company Industry"
            required
            options={industryOptions}
            loading={lists?.industries?.loading}
            valueField="label"
            labelField="label"
            error={errors.industry?.message}
          />

          <FormAutocomplete
            name="title"
            control={control}
            label="Job Title"
            required
            options={titleOptions}
            loading={lists?.titles?.loading}
            freeSolo
            valueField="id"
            labelField="label"
            error={errors.title?.message}
          />

          <FormTextField
            name="description"
            control={control}
            label="Bio"
            required
            error={errors.description?.message}
            textFieldProps={{
              multiline: true,
              rows: 4,
              placeholder: BIO_PLACEHOLDER,
            }}
          />

          <FormAutocomplete
            name="no_of_emp"
            control={control}
            label="Company Headcount"
            required
            options={noOfEmpOptions}
            loading={lists?.no_of_emps?.loading}
            valueField="id"
            labelField="label"
            error={errors.no_of_emp?.message}
          />
        </Box>

        <SaveAndCancelButton
          loading={submitting}
          onCancelButtonClick={onCancel}
        />
      </Stack>
    </form>
  );
}
